import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from "react";
import type {
  CommonLandingMwebData,
  TravelInsuranceTab,
  TravelProtectionTab,
} from "./CommonLandingMweb.types";
import {
  BulkBookingPersuasion,
  DropOffBottomSheet,
  HeroBar,
  PoweredBy,
  RecentSearches,
  StickyHeader,
  TravelInsuranceCard,
  TravelPlans,
  TravelProtectionTooltip,
  TripProtectionCarousel,
} from "./components";
import { COMMON_LANDING_CONSTANTS } from "./constants";
import {
  Content,
  FormContentGap,
  FormPanelShadow,
  Hero,
  MainContentContainer,
  PageContainer,
  StickyHeroBlock,
  StickyPanel,
  StickyPanelInner,
  Tab,
  Tabs,
  WhiteCard,
} from "./Styles";
// import Breadcrumb from "../../components/BreadcrumbComponent/BreadcrumbComponent";
import { navigationActions } from "@tm/insurance/navigation";
import { replaceHistory } from "@tm/insurance/utils";
import { usePlanType } from "@tm/ui";
import {
  buildRecentSearchNextPageRequest,
  navigateRecentSearchNextPage,
} from "../../utils/recentSearchNavigation";
import FormattedText from "../FormattedText/FormattedText";
import LandingFaq from "../LandingFaq/LandingFaq";
import SeoContent from "../SeoContent/SeoContent";
import type { RecentSearchItem } from "./components/RecentSearches/RecentSearches";
import type { TravelPlanItem } from "./components/TravelPlans/TravelPlans";
// import BreadcrumbComponent from "../BreadcrumbComponent";
// import { PARTNER } from "../../constants";
// import { useRouter } from "next/router";

type TabEntry = TravelInsuranceTab | TravelProtectionTab;

// const router = useRouter();
// const isTravelInsurancePage = router.asPath
//     .split("?")[0]
//     .includes("/travel-insurance");

function getUrlTravelPlan(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("travelPlan");
}

function deriveDefaultSelectedTabId(
  tabs: Array<TravelInsuranceTab | TravelProtectionTab> | undefined,
  urlTravelPlan?: string | null
): string {
  if (urlTravelPlan && tabs?.some((t) => t.id === urlTravelPlan))
    return urlTravelPlan;

  if (!tabs?.length) return COMMON_LANDING_CONSTANTS.TRAVEL_INSURANCE_TAB_ID;
  const list = tabs as TabEntry[];
  const selected = list.find((tab) => "isSelected" in tab && tab.isSelected);
  return (
    selected?.id ??
    tabs[0]?.id ??
    COMMON_LANDING_CONSTANTS.TRAVEL_INSURANCE_TAB_ID
  );
}

interface CommonLandingMwebProps {
  data?: CommonLandingMwebData;
  /**
   * `travelPlan` query param resolved on the server (via getServerSideProps).
   * Passed in so the initial render matches between SSR and client hydration
   * instead of reading `window.location.search` in the useState initializer.
   */
  initialTravelPlan?: string | null;
}

function useStickyOnScroll(threshold: number) {
  const [showSticky, setShowSticky] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > threshold);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return showSticky;
}

const CommonLandingMweb = ({
  data,
  initialTravelPlan,
}: CommonLandingMwebProps) => {
  const { setType } = usePlanType();
  const [selectedTab, setSelectedTab] = useState<string>(() =>
    deriveDefaultSelectedTabId(data?.travelPlanTabs, initialTravelPlan)
  );
  // const partnerType = getPartnerOnClientSide();
  const [showTooltip, setShowTooltip] = useState(false);
  const [showDropOffBottomSheet, setShowDropOffBottomSheet] = useState(false);
  const [countryDisplayText, setCountryDisplayText] = useState<string>(
    COMMON_LANDING_CONSTANTS.DEFAULT_SUBTITLE
  );
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [travellerCount, setTravellerCount] = useState<number | undefined>(
    undefined
  );
  const [selectedPlanType, setSelectedPlanType] = useState<string | undefined>(
    undefined
  );
  const explorePlansActionRef = useRef<(() => Promise<void>) | undefined>();

  const showSticky = useStickyOnScroll(
    COMMON_LANDING_CONSTANTS.STICKY_HEADER_SCROLL_THRESHOLD
  );

  useEffect(() => {
    const tabId = deriveDefaultSelectedTabId(
      data?.travelPlanTabs,
      getUrlTravelPlan()
    );
    setSelectedTab(tabId);
    if (typeof window !== "undefined") replaceHistory("travelPlan", tabId);
  }, [data?.travelPlanTabs]);

  useEffect(() => {
    if (data?.itineraryId) {
      sessionStorage.setItem("itineraryId", data.itineraryId);
    }
  }, [data?.itineraryId]);

  const dropOff = data?.dropOffBottomSheet;
  const backCounterRef = useRef(dropOff?.counter ?? 0);

  useEffect(() => {
    backCounterRef.current = dropOff?.counter ?? 0;
  }, [dropOff?.counter]);

  useEffect(() => {
    if (!dropOff?.showOnLand) return;
    if (backCounterRef.current > 0) {
      backCounterRef.current -= 1;
      setShowDropOffBottomSheet(true);
    }
  }, [dropOff?.showOnLand]);

  useEffect(() => {
    if (typeof window === "undefined" || !dropOff) return;

    window.history.pushState({ dropOffGuard: true }, "");

    const onPopState = () => {
      if (backCounterRef.current > 0) {
        backCounterRef.current -= 1;
        setShowDropOffBottomSheet(true);
        window.history.pushState({ dropOffGuard: true }, "");
      } else {
        window.history.back();
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [dropOff]);

  const handleDropOffGoBack = useCallback(() => {
    if (typeof window !== "undefined") window.history.back();
  }, []);

  const selectedTabData = useMemo(
    () => data?.travelPlanTabs?.find((tab) => tab.id === selectedTab),
    [data?.travelPlanTabs, selectedTab]
  );

  const isTravelInsurance =
    selectedTab === COMMON_LANDING_CONSTANTS.TRAVEL_INSURANCE_TAB_ID;

  const promoText = data?.heading?.subtitle;
  const hasHeadingPersuasion = Boolean(
    data?.headingPersuasion &&
      (data.headingPersuasion.stats != null || data.headingPersuasion.icon)
  );

  const heroBarProps = {
    title: data?.seoTitle || data?.heading?.title,
    headingPersuasion: data?.headingPersuasion,
    promoText,
    hasHeadingPersuasion,
  };

  const handleRecentSearchPress = useCallback(
    async (item: RecentSearchItem) => {
      try {
        await navigateRecentSearchNextPage(
          buildRecentSearchNextPageRequest(item, data?.shouldCallNextPageV2)
        );
      } catch (err) {
        console.error("Recent search navigation failed", err);
      }
    },
    []
  );

  const handleCountryChange = useCallback(
    (countries: Array<{ name?: string }> | undefined) => {
      const names = countries?.map((c) => c?.name).filter(Boolean);
      setCountryDisplayText(
        names?.length
          ? names.join(", ")
          : COMMON_LANDING_CONSTANTS.DEFAULT_SUBTITLE
      );
    },
    []
  );

  const handleDateChange = useCallback((start?: Date, end?: Date) => {
    setFromDate(start);
    setToDate(end);
  }, []);

  const handleSegmentChange = useCallback(
    (segments: Array<{ isSelected?: boolean; name?: string }> | undefined) => {
      setSelectedPlanType(segments?.find((s) => s?.isSelected)?.name);
    },
    []
  );

  const handleStickyExplorePlansPress = useCallback(() => {
    void explorePlansActionRef.current?.();
  }, []);

  const handleProtectionPlanPress = useCallback(
    (plan: TravelPlanItem) => {
      setType(plan.type);
      if (
        typeof window !== "undefined" &&
        window.location.pathname.includes("/travel-insurance")
      ) {
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set("type", plan.type || "");
        window.location.href = `${
          window.location.origin
        }/insurance?${queryParams.toString()}`;
      } else {
        navigationActions.navigateToLanding({ type: plan?.type }, false);
      }
    },
    [setType]
  );

  const travelPlanTabs = data?.travelPlanTabs || [];

  return (
    <PageContainer>
      <Hero>
        <HeroBar {...heroBarProps} />
      </Hero>

      <Content>
        <FormPanelShadow $pullUp={hasHeadingPersuasion}>
          <WhiteCard>
            {travelPlanTabs.length >= 2 ? (
              <Tabs>
                {travelPlanTabs.slice(0, 2).map((tab) => (
                  <Tab
                    key={tab.id}
                    $active={selectedTab === tab.id}
                    onClick={() => {
                      setSelectedTab(tab.id);
                      if (typeof window !== "undefined")
                        replaceHistory("travelPlan", tab.id);
                    }}
                  >
                    <FormattedText value={tab.title} />
                  </Tab>
                ))}
              </Tabs>
            ) : null}
            <FormContentGap>
              {isTravelInsurance ? (
                <TravelInsuranceCard
                  travelInsuranceData={selectedTabData as TravelInsuranceTab}
                  onCountryChange={handleCountryChange}
                  onDateChange={handleDateChange}
                  onTravellerChange={setTravellerCount}
                  onSegmentChange={handleSegmentChange}
                  onCTAPress={() => void 0}
                  isV2Endpoint={data?.shouldCallNextPageV2}
                  explorePlansActionRef={explorePlansActionRef}
                />
              ) : (
                <TravelPlans
                  plans={(selectedTabData as TravelProtectionTab)?.plans}
                  isVisible
                  onPlanPress={handleProtectionPlanPress}
                />
              )}
            </FormContentGap>
          </WhiteCard>
        </FormPanelShadow>

        <MainContentContainer>
          <BulkBookingPersuasion data={data?.bulkBookingPersuasion} />
          <RecentSearches
            title={data?.recentSearches?.title}
            recentSearches={
              data?.recentSearches?.data as RecentSearchItem[] | undefined
            }
            onSearchPress={handleRecentSearchPress}
          />
          <TripProtectionCarousel
            title={data?.carouselCards?.title}
            cards={data?.carouselCards?.cards}
          />
          <PoweredBy
            title={data?.poweredBy?.title}
            icon={data?.poweredBy?.icon}
            iconsList={data?.poweredBy?.iconsList}
            description={data?.poweredBy?.descrption}
          />
          {data?.seoContent && <SeoContent seoContent={data?.seoContent} />}
          {/* {isTravelInsurancePage && (
            <BreadcrumbComponent
              hostname={
                partnerType === PARTNER.GI
                  ? "www.goibibo.com"
                  : "www.makemytrip.com"
              }
              label={breadcrumbLabel || undefined}
            />
          )} */}
          {(data?.faq || data?.seoFaq) && (
            <LandingFaq
              customTitle={data?.seoFaq?.title || data?.faq?.title}
              faq={
                (data?.seoFaq?.faqList as ComponentProps<
                  typeof LandingFaq
                >["faq"]) ||
                (data?.faq?.faqList as ComponentProps<typeof LandingFaq>["faq"])
              }
              customInfo={data?.seoFaq?.info || data?.faq?.info}
              icon={data?.seoFaq?.icon || data?.faq?.icon}
              isCardView
            />
          )}
        </MainContentContainer>
      </Content>

      {isTravelInsurance ? (
        <StickyPanel $show={showSticky} aria-hidden={!showSticky}>
          <StickyHeroBlock>
            <HeroBar {...heroBarProps} titleAsDiv />
          </StickyHeroBlock>
          <StickyPanelInner>
            <StickyHeader
              heading={data?.heading}
              selectedCountry={countryDisplayText}
              ctaLabel={(selectedTabData as any)?.onScrollData?.ctaText}
              editLabel={(selectedTabData as any)?.onScrollData?.editLabel}
              fromDate={fromDate}
              toDate={toDate}
              travellerCount={travellerCount}
              selectedPlanType={selectedPlanType}
              onExplorePlansPress={handleStickyExplorePlansPress}
            />
          </StickyPanelInner>
        </StickyPanel>
      ) : null}

      <TravelProtectionTooltip
        visible={showTooltip}
        tooltipData={data?.toolTip}
        onClose={() => setShowTooltip(false)}
      />

      <DropOffBottomSheet
        visible={showDropOffBottomSheet}
        setVisible={setShowDropOffBottomSheet}
        dropOffData={data?.dropOffBottomSheet}
        showOnLand={Boolean(dropOff?.showOnLand)}
        onGoBack={handleDropOffGoBack}
      />
    </PageContainer>
  );
};

export default CommonLandingMweb;
